/**
 * Thin adapter over {@link useDataAccumulatorForSingleOrder} that unwraps the
 * per-domain response envelopes (`{ order }`, `{ auth_on_file }`, `{ coverages }`,
 * `{ submission }`, `{ entries }`) into directly-typed values.
 *
 * Centralising the unwrap removes the ~12 repeated `?.order ?? undefined` access
 * patterns in {@link SingleOrderLayout} and lets {@link useOrderAccessPermission}
 * receive a properly-typed `GeneralOrderDetails | null` without an unsafe cast.
 *
 * IMPORTANT: every unwrapped value is returned as `T | null` (never a fresh
 * fallback like `[]` or `{}`). A `?? []` substitution would create a brand-new
 * array reference on each render, which — when fed into a downstream effect's
 * dependency array (see {@link useSingleOrderInitialDataSync}) — causes an
 * infinite render loop. Callers are expected to apply their own non-reactive
 * fallback at the use-site instead.
 *
 * @module useSingleOrderUnwrappedData
 */
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import {
  AuthOnFile,
  ClinicalDataEntry,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";

/** Shape returned by {@link useSingleOrderUnwrappedData}. */
export interface SingleOrderUnwrappedData {
  order: GeneralOrderDetails | null;
  authOnFile: AuthOnFile | null;
  coverages: SingleCoverage[] | null;
  submission: Submission | null;
  /** `null` until the clinical-data fetch resolves; never an empty array (see module doc). */
  clinicalEntries: ClinicalDataEntry[] | null;
  isLoading: boolean;
  generalOrderDetailsError: unknown;
  fetchAll: (
    orderId: string,
    orgId: string,
    options?: { force?: boolean },
  ) => Promise<void>;
  abortAll: () => void;
  fetchAuthOnFileDetails: (orderId: string, orgId: string) => Promise<unknown>;
}

/**
 * Reads the data-accumulator context and returns the unwrapped, directly-typed
 * domain values plus the lifecycle handles needed by {@link SingleOrderLayout}.
 *
 * @returns Unwrapped domain data and lifecycle handles
 */
export const useSingleOrderUnwrappedData = (): SingleOrderUnwrappedData => {
  const {
    generalOrderDetails,
    authOnFileDetails,
    coverageDetails,
    submissionData,
    clinicalData,
    loading: isLoading,
    generalOrderDetailsError,
    fetchAll,
    abortAll,
    fetchAuthOnFileDetails,
  } = useDataAccumulatorForSingleOrder();

  return {
    order: generalOrderDetails?.order ?? null,
    authOnFile: authOnFileDetails?.auth_on_file ?? null,
    coverages: coverageDetails?.coverages ?? null,
    submission: submissionData?.submission ?? null,
    clinicalEntries: clinicalData?.entries ?? null,
    isLoading,
    generalOrderDetailsError,
    fetchAll,
    abortAll,
    fetchAuthOnFileDetails,
  };
};
